import React, { useEffect, useRef, useState } from 'react';

// 섹션 3-4: Excel Section과 Function Section을 원본 HTML과 완전히 동일하게 구현
const Section3and4: React.FC = () => {
  const excelSectionRef = useRef<HTMLElement>(null);
  const [isExcelAnimating, setIsExcelAnimating] = useState(false);

  // Excel 테이블 데이터 (원본 HTML과 동일)
  const excelData = [
    {
      category: '거푸집',
      itemName: '합판거푸집',
      spec: '간단/1회',
      unit: 'm²',
      quantity: '32',
      unitPrice: '3,125',
      total: '100,000'
    },
    {
      category: '토공',
      itemName: '터파기',
      spec: 'Type-3',
      unit: 'm³',
      quantity: '111',
      unitPrice: '451',
      total: '50,000'
    },
    {
      category: '철근',
      itemName: '철근가공',
      spec: 'D13/일반',
      unit: 'ton',
      quantity: '2.5',
      unitPrice: '300,000',
      total: '750,000'
    },
    {
      category: '콘크리트',
      itemName: '콘크리트타설',
      spec: '25-24-150',
      unit: 'm³',
      quantity: '85',
      unitPrice: '3,765',
      total: '320,000'
    }
  ];

  // 핵심 기능 데이터 (원본 HTML과 동일)
  const coreFeatures = [
    {
      icon: '🔍',
      title: '일위대가 자동 검증',
      description: 'AI가 일위대가 구성 요소를 분석하여 표준품셈과 자동 대조 검증합니다.',
      stats: [
        { value: '99%', label: '검증정확도' },
        { value: '3분', label: '처리시간' }
      ]
    },
    {
      icon: '📋',
      title: '표준품셈 연동',
      description: '최신 표준품셈 PDF와 실시간 연동하여 정확한 기준으로 검증합니다.',
      stats: [
        { value: '2025', label: '최신기준' },
        { value: '100%', label: '실시간연동' }
      ]
    },
    {
      icon: '📊',
      title: '상세 검증 리포트',
      description: '수량 차이, 누락 항목, 개선 제안까지 포함된 상세 검증 결과를 제공합니다.',
      stats: [
        { value: 'PDF', label: '리포트' },
        { value: '즉시', label: '다운로드' }
      ]
    },
    {
      icon: '🛡️',
      title: '데이터 보안',
      description: '건설 프로젝트 데이터를 암호화하여 안전하게 보관하고 관리합니다.',
      stats: [
        { value: '256bit', label: '암호화' },
        { value: '99.9%', label: '안전성' }
      ]
    },
    {
      icon: '⚡',
      title: '배치 검증',
      description: '프로젝트 내 모든 일위대가를 한 번에 일괄 검증하여 효율성을 극대화합니다.',
      stats: [
        { value: '1000+', label: '동시처리' },
        { value: '95%', label: '시간단축' }
      ]
    },
    {
      icon: '📱',
      title: '모바일 접근',
      description: '건설 현장에서도 모바일로 검증 결과를 확인하고 리포트를 다운로드합니다.',
      stats: [
        { value: 'PWA', label: '앱지원' },
        { value: '24H', label: '접근가능' }
      ]
    }
  ];

  // 스크롤 애니메이션 초기화
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Excel 애니메이션 자동 시작 및 반복
  useEffect(() => {
    let animationInterval: NodeJS.Timeout | null = null;
    let isInView = false;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isExcelAnimating) {
            isInView = true;
            // 처음 시작
            setTimeout(() => {
              startExcelAnimation();
            }, 1000);
            
            // 애니메이션 사이클: 0.5초(추가) + 4초(대기) + 0.5초(fadeout) + 1초(대기) = 6초
            animationInterval = setInterval(() => {
              if (isInView && !isExcelAnimating) {
                startExcelAnimation();
              }
            }, 6000);
          } else if (!entry.isIntersecting) {
            isInView = false;
            // 뷰포트를 벗어나면 애니메이션 중지
            if (animationInterval) {
              clearInterval(animationInterval);
              animationInterval = null;
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    const excelElement = document.getElementById('excel');
    if (excelElement) {
      observer.observe(excelElement);
    }

    return () => {
      observer.disconnect();
      if (animationInterval) {
        clearInterval(animationInterval);
      }
    };
  }, [isExcelAnimating]);

  const startExcelAnimation = async () => {
    if (isExcelAnimating) return;
    setIsExcelAnimating(true);

    const tbody = document.getElementById('tableBody');
    if (!tbody) {
      setIsExcelAnimating(false);
      return;
    }

    // 기존 복제 행 제거
    const existingClones = tbody.querySelectorAll('tr[data-cloned="true"]');
    existingClones.forEach(row => row.remove());

    // 원본 행들을 저장
    const originalRows = Array.from(tbody.querySelectorAll('tr[data-original="true"]'));
    const clonedRows: HTMLElement[] = [];

    // 모든 행을 한번에 복제하여 추가 (순차적이 아닌 동시에)
    originalRows.forEach((originalRow) => {
      const row = originalRow as HTMLElement;
      
      // 복제된 행 생성
      const clonedRow = row.cloneNode(true) as HTMLElement;
      clonedRow.classList.add('cloned-row');
      clonedRow.removeAttribute('data-original');
      clonedRow.setAttribute('data-cloned', 'true');
      
      // 초기 스타일 설정 (투명하고 작게)
      clonedRow.style.opacity = '0';
      clonedRow.style.transform = 'translateY(-20px) scale(0.95)';
      clonedRow.style.transition = 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
      
      // 모든 td에 초록색 스타일 적용
      const clonedCells = clonedRow.querySelectorAll('td');
      clonedCells.forEach(cell => {
        (cell as HTMLElement).style.color = '#dc2626';
        (cell as HTMLElement).style.fontWeight = '600';
        (cell as HTMLElement).style.background = 'rgba(34, 197, 94, 0.05)';
        (cell as HTMLElement).style.transition = 'all 0.5s ease';
      });
      
      // 복제된 행을 원본 행 바로 다음에 삽입
      row.insertAdjacentElement('afterend', clonedRow);
      clonedRows.push(clonedRow);
    });

    // 모든 행을 동시에 나타나게 함 (0.1초 후)
    setTimeout(() => {
      clonedRows.forEach(clonedRow => {
        clonedRow.style.opacity = '1';
        clonedRow.style.transform = 'translateY(0) scale(1)';
        clonedRow.style.background = 'rgba(34, 197, 94, 0.08)';
      });
      
      // 하이라이트 효과 제거
      setTimeout(() => {
        clonedRows.forEach(clonedRow => {
          clonedRow.style.background = 'rgba(34, 197, 94, 0.03)';
        });
      }, 500);
    }, 100);

    // 4초 대기 후 fadeout (추가 애니메이션의 반대로)
    setTimeout(() => {
      clonedRows.forEach(clonedRow => {
        clonedRow.style.transition = 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
        clonedRow.style.opacity = '0';
        clonedRow.style.transform = 'translateY(-20px) scale(0.95)';
        clonedRow.style.background = 'rgba(34, 197, 94, 0.01)';
      });
      
      // fadeout 완료 후 행 제거 (0.5초 후)
      setTimeout(() => {
        clonedRows.forEach(clonedRow => {
          clonedRow.remove();
        });
        
        // 1초 대기 후 애니메이션 상태 리셋
        setTimeout(() => {
          setIsExcelAnimating(false);
        }, 1000);
      }, 500);
    }, 4000);
  };

  return (
    <>
      {/* 3. 엑셀 데모 섹션 */}
      <section className="section excel-section scroll-animate" id="excel" ref={excelSectionRef}>
        <div className="section-container">
          <div style={{textAlign: 'center', marginBottom: '60px'}}>
            <div className="status-badge">
              <span>⚡</span>
              <span>FLOW EXCEL ANIMATION</span>
            </div>
            
            <h1 style={{
              fontSize: 'clamp(36px, 5vw, 56px)', 
              fontWeight: '900', 
              marginBottom: '16px', 
              color: '#0f172a'
            }}>
              파티클이 흐르는<br/>
              <span className="title-highlight">엑셀 행 복제</span> 마법
            </h1>
            
            <p style={{
              fontSize: 'clamp(16px, 2vw, 20px)', 
              color: '#475569', 
              marginBottom: '40px'
            }}>
              데이터가 흐르는 플로우 속에서 엑셀 행이 부드럽게 복제됩니다
            </p>
          </div>

          <div className="excel-table-container">
            <table className="excel-table" id="excelTable">
              <thead>
                <tr>
                  <th>분류</th>
                  <th>품명</th>
                  <th>규격</th>
                  <th>단위</th>
                  <th>수량</th>
                  <th>단가</th>
                  <th>합계</th>
                </tr>
              </thead>
              <tbody id="tableBody">
                {excelData.map((row, index) => (
                  <tr key={index} data-original="true">
                    <td>{row.category}</td>
                    <td>{row.itemName}</td>
                    <td>{row.spec}</td>
                    <td>{row.unit}</td>
                    <td>{row.quantity}</td>
                    <td>{row.unitPrice}</td>
                    <td>{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 4. 핵심 기능 섹션 */}
      <section className="section function-section scroll-animate" id="function">
        <div className="section-container">
          <div style={{textAlign: 'center', marginBottom: '80px'}}>
            <div style={{
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '10px', 
              background: 'rgba(37, 99, 235, 0.08)', 
              color: '#2563eb', 
              padding: '12px 24px', 
              borderRadius: '40px', 
              fontSize: '13px', 
              fontWeight: '700', 
              letterSpacing: '0.6px', 
              marginBottom: '24px', 
              border: '1px solid rgba(37, 99, 235, 0.15)', 
              animation: 'badgePulse 4s ease-in-out infinite'
            }}>
              <span>🏗️</span>
              <span>CONSTRUCTION FEATURES</span>
            </div>
            
            <h1 style={{
              fontSize: 'clamp(42px, 5vw, 64px)', 
              fontWeight: '900', 
              lineHeight: '1.2', 
              marginBottom: '20px', 
              color: '#0f172a'
            }}>
              <span style={{
                background: 'linear-gradient(135deg, #2563eb, #10b981, #059669)', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent', 
                backgroundClip: 'text', 
                backgroundSize: '200% 200%', 
                animation: 'gradientShift 8s ease-in-out infinite'
              }}>스마트 기능</span>으로<br/>
              건설 견적 혁신
            </h1>
            
            <p style={{
              fontSize: 'clamp(18px, 2.2vw, 24px)', 
              color: '#64748b', 
              maxWidth: '700px', 
              margin: '0 auto', 
              lineHeight: '1.6'
            }}>
              콘엑스의 혁신적인 AI 기술이 제공하는<br/>
              정확하고 효율적인 건설 견적 솔루션
            </p>
          </div>

          <div style={{maxWidth: '1400px', margin: '0 auto'}}>
            <div className="features-grid">
              {coreFeatures.map((feature, index) => (
                <div key={index} className="core-feature-item scroll-animate">
                  <div className="feature-header">
                    <div className="feature-icon">{feature.icon}</div>
                    <h3 style={{
                      fontSize: '20px', 
                      fontWeight: '700', 
                      color: '#1e293b', 
                      lineHeight: '1.3'
                    }}>
                      {feature.title}
                    </h3>
                  </div>
                  
                  <p style={{
                    fontSize: '15px', 
                    color: '#64748b', 
                    lineHeight: '1.6', 
                    marginBottom: '20px'
                  }}>
                    {feature.description}
                  </p>
                  
                  <div className="feature-stats">
                    {feature.stats.map((stat, statIndex) => (
                      <div key={statIndex} className="stat-item">
                        <span className="stat-value">{stat.value}</span>
                        <span className="stat-label">{stat.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Section3and4;